import { BufferGeometry, Mesh, Object3D } from '@seddi/three';
import * as FlagsModule from '../flags/flagsModule';
import { FeatureFlag } from '../flags/flagsTypes';
import { ResourcesContext } from '../resources/resourcesContext';

export interface MeshRenderable {
  geometry?: BufferGeometry;
  materialRef?: string;
  mesh: Mesh;
  meshRef?: string;
}

/**
 * Create empty renderable.
 * @param parent - Parent node.
 */
export const create = (id: string, node: Object3D, tag?: string): MeshRenderable => {
  const mesh = new Mesh();
  mesh.name = id + '-mesh';
  node.add(mesh);

  // Depth peeling fix: Avoid having artifacts on object "scenery"
  if (FlagsModule.isEnabled(FeatureFlag.DEPTH_PEELING) && tag === 'Scenery') {
    mesh.userData.depthPeelingBackground = true;
  }

  return { mesh };
};

/**
 * Attach material.
 * @param renderable - Renderable.
 * @param id - Material ID.
 * @param resources - Resources context.
 */
export const attachMaterial = (
  renderable: MeshRenderable,
  id: string,
  resources: ResourcesContext,
) => {
  // Return previous reference.
  if (renderable.materialRef) {
    detachMaterial(renderable, resources);
  }
  // Attach.
  const resource = resources.getRefMaterial(id);
  renderable.mesh.material = resource;
  renderable.mesh.material.needsUpdate = true;
  renderable.materialRef = id;
};

/**
 * Attach mesh.
 * @param renderable - Renderable.
 * @param id - Mesh ID.
 * @param resources - Resources context.
 */
export const attachMesh = (renderable: MeshRenderable, id: string, resources: ResourcesContext) => {
  // Return previous reference.
  if (renderable.meshRef) {
    detachMesh(renderable, resources);
  }
  // Attach.
  const geometry = resources.getRefMesh(id);
  renderable.mesh.geometry = geometry;
  renderable.meshRef = id;
};

/**
 * Detach material.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const detachMaterial = (renderable: MeshRenderable, resources: ResourcesContext) => {
  if (!renderable.materialRef) {
    return;
  }
  resources.retRefMaterial(renderable.materialRef);
  delete renderable.materialRef;
  renderable.mesh.material = resources.materialDef;
};

/**
 * Detach mesh.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const detachMesh = (renderable: MeshRenderable, resources: ResourcesContext) => {
  if (!renderable.meshRef) {
    return;
  }
  resources.retRefMesh(renderable.meshRef);
  delete renderable.meshRef;
};

/**
 * Dispose renderable.
 * @param renderable - Renderable.
 * @param context - Resources context.
 */
export const dispose = (renderable: MeshRenderable, context: ResourcesContext) => {
  const mesh = renderable.mesh;
  detachMaterial(renderable, context);
  detachMesh(renderable, context);

  if (renderable.geometry) {
    renderable.geometry.dispose();
  }

  mesh.parent.remove(mesh);
};
